import { FolderFormatsDto, FolderTemplateFormatId, FormatFoldersDto } from "@common/dto/folder-settings";
import client, { clientTransformed } from '@common/utils/rest';
import { getQueriesAsSearch } from '@common/utils';
import { FolderListDto } from "@common/dto/folder/list-dto";
import { HttpSuccessDto } from "@common/dto/http";
import { FolderPrimaryListDto } from "@common/dto/folder/primary-list-dto";
import { FieldListDto } from "@common/dto/field/list-dto";
import { FieldPrimaryListDto } from "@common/dto/field/list-primary-dto";
import { DatasheetFolderListDto } from "@common/dto/folder/datasheet-list-dto";
import { DatasheetFormatListDto } from "@common/dto/format/datasheet-list-dto";

enum FolderContext {
    ids = 1,
    pds = 2,
}

export type CreateFormatFolderParams = {
    name: string;
    parentId: number;
}

export type FolderFormatConfig = {
    path: string;
    folderFormatsPath?: string;
    deleteFormatPath: string;
    deleteFormatFolderPath: string;
    createFormatFolderPath: string;
    queries?: {
        context?: FolderContext;
        folderId?: FolderListDto['id'];
        parentFolderId?: FolderListDto['id'];
        parentId?: FolderListDto['id'];
        folderName?: string;
        header?: boolean;
        formatId?: number;
        tds?: boolean;
        tdsFolder?: boolean;
    }
}

const FOLDERS_FORMATS_CONFIG: Record<keyof FolderTemplateFormatId, FolderFormatConfig> = {
    defaultIdsFormatId: {
        path: '/ds',
        deleteFormatPath: '/deleteDsFormat',
        deleteFormatFolderPath: '/deleteDsFolder',
        createFormatFolderPath: '/saveDSFolder',
        queries: {
            context: FolderContext.ids,
            tds: false,
            tdsFolder: false,
        }
    },
    defaultPdsFormatId: {
        path: '/ds',
        deleteFormatPath: '/deleteDsFormat',
        deleteFormatFolderPath: '/deleteDsFolder',
        createFormatFolderPath: '/saveDSFolder',
        queries: {
            context: FolderContext.pds,
            tds: false,
            tdsFolder: false,
        }
    },
    defaultSubtextFormatId: {
        deleteFormatPath: '/deleteSubtextFormat',
        deleteFormatFolderPath: '/deleteSubtextFormatFolder',
        createFormatFolderPath: '/saveSubtextFormatFolders',
        path: '/subtext',
    },
    defaultSymbolsFormatId: {
        deleteFormatPath: '/deleteSymbolsFormat',
        deleteFormatFolderPath: '/deleteSymbolsFormatFolder',
        createFormatFolderPath: '/saveSymbolsFormatsFolders',
        path: '/symbols',
    },
    defaultPedDropFormatId: {
        deleteFormatPath: '/deleteDropFieldsFormat',
        deleteFormatFolderPath: '/deleteDropFieldsFormatFolder',
        createFormatFolderPath: '/saveDropFieldFormatFolder',
        path: '/dropfields',
    },
    defaultHeaderFormatId: {
        path: '/headerfooterformatsfolders',
        folderFormatsPath: '/headerfooterformats',
        deleteFormatPath: '/deleteHeaderFooterFormat',
        deleteFormatFolderPath: '/deleteHeaderFooterFormatFolder',
        createFormatFolderPath: '/saveHeaderFooterFormatFolder',
        queries: {
            parentFolderId: 0,
            header: true
        }
    },
    defaultFooterFormatId: {
        path: '/headerfooterformatsfolders',
        folderFormatsPath: '/headerfooterformats',
        deleteFormatPath: '/deleteHeaderFooterFormat',
        deleteFormatFolderPath: '/deleteHeaderFooterFormatFolder',
        createFormatFolderPath: '/saveHeaderFooterFormatFolder',
        queries: {
            parentFolderId: 0,
            header: false,
        }
    }
};

export const getFolderFormats = (type: keyof FolderTemplateFormatId, queries?: FolderFormatConfig['queries']) => {
    const requestConfig = FOLDERS_FORMATS_CONFIG[type];
    const { folderFormatsPath, path } = requestConfig;
    const allQueries = { ...requestConfig.queries, ...queries };
    const finalPath = `api/formats${folderFormatsPath || path}${getQueriesAsSearch(allQueries)}`;

    return client({
        path: finalPath,
        method: 'GET'
    }).then((res: HttpSuccessDto<FolderFormatsDto[]>) => res.entity);
};

export const getFormatFolders = (type: keyof FolderTemplateFormatId) => {
    const { path, queries } = FOLDERS_FORMATS_CONFIG[type];
    const finalPath = `api/folders/formats${path}${getQueriesAsSearch(queries)}`;

    return client({
        path: finalPath,
        method: 'GET'
    }).then((res: HttpSuccessDto<FormatFoldersDto[]>) => res.entity);
};

type FormatIdsOnUpdateFolderWithQuery = keyof Pick<FolderTemplateFormatId,'defaultIdsFormatId' | 'defaultPdsFormatId'>;
type FormatIdsHeaderFooter = keyof Pick<FolderTemplateFormatId,'defaultHeaderFormatId' | 'defaultFooterFormatId'>;

export const createFormatFolder = (type: keyof FolderTemplateFormatId, { name, parentId }: CreateFormatFolderParams) => {
    const formatIds: Array<FormatIdsOnUpdateFolderWithQuery> = ['defaultIdsFormatId', 'defaultPdsFormatId'];
    const formatIdsFooterHeader: Array<FormatIdsHeaderFooter>  = ['defaultHeaderFormatId', 'defaultFooterFormatId'];

    let entity;
    let queries;

    if (formatIds.includes(type as FormatIdsOnUpdateFolderWithQuery)) {
        queries = { folderName: name, parentId }
    } else if (formatIdsFooterHeader.includes(type as FormatIdsHeaderFooter)) {
        entity = { folderName: name, parentId };
    } else {
        entity = { foldername: name, parentid: parentId };
    }

    const requestConfig = FOLDERS_FORMATS_CONFIG[type];
    const { createFormatFolderPath } = requestConfig;
    const { context, tds, ...requiredQueries } = requestConfig.queries || {};
    const allQueries = { fieldContext: context, ...requiredQueries, ...queries };
    const finalPath = `api/folders/formats${createFormatFolderPath}${getQueriesAsSearch(allQueries)}`;

    return client({
        path: finalPath,
        method: 'POST',
        entity,
    });
};

export const deleteFormatFolder = (type: keyof FolderTemplateFormatId, queries?: FolderFormatConfig['queries']) => {
    const requestConfig = FOLDERS_FORMATS_CONFIG[type];
    const { deleteFormatFolderPath } = requestConfig;
    const allQueries = { ...requestConfig.queries, ...queries };

    const finalPath = `api/folders/formats${deleteFormatFolderPath}${getQueriesAsSearch(allQueries)}`;

    return client({
        path: finalPath,
        method: 'POST',
    });
};

export const deleteFormat = (type: keyof FolderTemplateFormatId, queries?: FolderFormatConfig['queries']) => {
    const requestConfig = FOLDERS_FORMATS_CONFIG[type];
    const { deleteFormatPath } = requestConfig;
    const allQueries = { ...requestConfig.queries, ...queries };

    const finalPath = `api/folders/formats${deleteFormatPath}${getQueriesAsSearch(allQueries)}`;

    return client({
        path: finalPath,
        method: 'POST',
    });
};

export const getModuleFolderFields = ({ 
    searchQuery, 
    folderId, 
    system, 
    contextId 
}: { 
    searchQuery?: string; 
    folderId?: FolderPrimaryListDto['id'];
    system: boolean;
    contextId: number 
}): Promise<Array<FieldListDto>> => {
  return clientTransformed({
    path: "api/fieldsResource/load/fields",
    params: {
        searchQuery,
        parentId: folderId,
        contextId,
        system,
    },
    method: "GET",
  }).then((response: HttpSuccessDto<Array<FieldListDto>>) => {
    return response.entity;
  });
};

export type GetFolderFieldsParams = {
    searchQuery?: string; 
    folderId?: FolderPrimaryListDto['id'];
    contextId: number;
    includeTables?: boolean;
};

export const getFolderFields = ({ searchQuery, folderId, contextId, includeTables }: GetFolderFieldsParams): Promise<Array<FieldPrimaryListDto>> => {
  return clientTransformed({
    path: "api/fields",
    params: {
        query: searchQuery,
        folderId,
        conext: contextId,
        includeTables,
    },
    method: "GET",
  }).then((response: HttpSuccessDto<Array<FieldPrimaryListDto>>) => {
    return response.entity;
  });
};

export type GetDatasheetFolderFormats = {
    folderId: DatasheetFolderListDto['folderId']; 
    context: DatasheetFolderListDto['fieldContext'];
};

export const getDatasheetFolderFormats = (params: GetDatasheetFolderFormats): Promise<Array<DatasheetFormatListDto>> => {
    return client({
        path: 'api/formats/ds',
        method: 'GET',
        params: params
    }).then((res: HttpSuccessDto<Array<DatasheetFormatListDto>>) => {
        return res.entity;
    });
};